import { useState } from 'react';
import { mensagemDoErro } from '../../api/erros.js';
import {
  configurarSincronizacaoPeriodica,
  obterSincronizacaoPeriodica,
} from '../../api/sincronizacao.js';
import type { SincronizacaoPeriodica } from '../../api/sincronizacao.js';
import { useConsulta } from '../../api/useConsulta.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { CampoTexto } from '../../componentes/Campo.js';
import { Cartao } from '../../componentes/Cartao.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { FalhaDeCarregamento } from '../../componentes/FalhaDeCarregamento.js';
import { seHouver } from '../../componentes/props.js';
import { intervaloDoTexto } from './situacao.js';

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

type Props = {
  /** Só o Administrador liga, desliga e muda o intervalo (`sincronizacao.gerenciar`). */
  readonly podeConfigurar: boolean;
};

/**
 * Sincronização periódica com o ERP (r1-sync).
 *
 * A conclusão da carga inicial já a liga; daí em diante, um ciclo a cada
 * intervalo traz o que mudou no ERP. O cartão diz quando foi o último ciclo
 * sem falha, quando é o próximo, e quantas falhas seguidas houve — uma
 * sincronização que falha calada é um armazém lendo dado velho.
 */
export function CartaoSincronizacaoPeriodica({ podeConfigurar }: Props) {
  const consulta = useConsulta(obterSincronizacaoPeriodica);
  const { recarregar } = consulta;
  const [aviso, definirAviso] = useState<Aviso | null>(null);
  const [rascunho, definirRascunho] = useState<string | null>(null);
  const [erroDoCampo, definirErroDoCampo] = useState<string | null>(null);
  const [agindo, definirAgindo] = useState(false);

  function salvar(ativa: boolean, intervaloMinutos: number, sucesso: string) {
    definirAgindo(true);
    definirAviso(null);

    configurarSincronizacaoPeriodica({ ativa, intervaloMinutos })
      .then(() => {
        definirAgindo(false);
        definirRascunho(null);
        definirAviso({ tom: 'ok', texto: sucesso });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirAviso({ tom: 'erro', texto: mensagemDoErro(falha) });
      });
  }

  function alternar(atual: SincronizacaoPeriodica) {
    const ativa = !atual.ativa;
    salvar(
      ativa,
      atual.intervaloMinutos,
      ativa
        ? 'Sincronização periódica ligada.'
        : 'Sincronização periódica desligada. O painel deixa de ler o ERP até religar.',
    );
  }

  function salvarIntervalo(atual: SincronizacaoPeriodica, texto: string) {
    const lido = intervaloDoTexto(texto);

    if ('erro' in lido) {
      definirErroDoCampo(lido.erro);
      return;
    }

    definirErroDoCampo(null);
    salvar(
      atual.ativa,
      lido.valor,
      `Intervalo salvo: a cada ${String(lido.valor)} min, contados do fim de um ciclo.`,
    );
  }

  const periodica = consulta.dados;

  return (
    <Cartao titulo="Sincronização periódica">
      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      {consulta.erro !== null ? (
        <FalhaDeCarregamento mensagem={consulta.erro} aoTentarDeNovo={recarregar} />
      ) : periodica === null ? (
        <p className="sincronizacao__discreto" role="status">
          Carregando a sincronização periódica…
        </p>
      ) : (
        <div className="sincronizacao__carga">
          <EstadoDaPeriodica periodica={periodica} />

          {podeConfigurar && (
            <div className="sincronizacao__configuracao">
              <CampoTexto
                rotulo="Intervalo (minutos)"
                valor={rascunho ?? String(periodica.intervaloMinutos)}
                aoMudar={definirRascunho}
                fonte="mono"
                ajuda="De 1 a 1440 (um dia)."
                {...seHouver('erro', erroDoCampo)}
              />
              <span className="sincronizacao__acoes">
                <Botao
                  variante="primario"
                  disabled={agindo}
                  onClick={() => {
                    salvarIntervalo(periodica, rascunho ?? String(periodica.intervaloMinutos));
                  }}
                >
                  Salvar intervalo
                </Botao>
                <Botao
                  variante={periodica.ativa ? 'atencao' : 'sucesso-solido'}
                  disabled={agindo}
                  onClick={() => {
                    alternar(periodica);
                  }}
                >
                  {periodica.ativa ? 'Desligar' : 'Ligar'}
                </Botao>
              </span>
            </div>
          )}
        </div>
      )}
    </Cartao>
  );
}

function EstadoDaPeriodica({ periodica }: { readonly periodica: SincronizacaoPeriodica }) {
  return (
    <>
      <p className="sincronizacao__estado">
        {periodica.ativa ? (
          <ChipStatus tom="concluida" rotulo="Ligada" />
        ) : (
          <ChipStatus tom="desativado" rotulo="Desligada" />
        )}
        {periodica.emExecucao && <ChipStatus tom="andamento" rotulo="Rodando agora" />}
        <span className="sincronizacao__discreto">A cada {periodica.intervaloMinutos} min</span>
      </p>

      {periodica.ultimoErro !== null && (
        <AvisoDaTela tom="atencao">Último erro: {periodica.ultimoErro}</AvisoDaTela>
      )}

      <dl className="sincronizacao__fatos">
        <div>
          <dt>Último sincronismo</dt>
          <dd>{formatarDataHora(periodica.sincronizadoEm, 'nunca')}</dd>
        </div>
        <div>
          <dt>Próxima execução</dt>
          <dd>{formatarDataHora(periodica.proximaExecucaoEm)}</dd>
        </div>
        <div>
          <dt>Falhas seguidas</dt>
          <dd
            className={
              periodica.falhasConsecutivas > 0 ? 'sincronizacao__fato--atencao' : undefined
            }
          >
            {periodica.falhasConsecutivas}
          </dd>
        </div>
      </dl>
    </>
  );
}
